import { signInAnonymously, onAuthStateChange, getCurrentUser } from '@paulownia/shared/lib/auth';

let customerIdCache: string | null = null;
let authInitPromise: Promise<string> | null = null;

/**
 * Initialize anonymous authentication for the customer
 * Creates or restores an anonymous Firebase user
 * Returns the customer ID (anonymous user UID)
 */
export async function initCustomerAuth(): Promise<string> {
  // Return cached promise if already initializing
  if (authInitPromise) {
    return authInitPromise;
  }

  authInitPromise = new Promise<string>((resolve, reject) => {
    // Check if already signed in
    const currentUser = getCurrentUser();
    if (currentUser) {
      customerIdCache = currentUser.uid;
      resolve(currentUser.uid);
      return;
    }

    // Listen for auth state once to check for restored session
    const unsubscribe = onAuthStateChange(async (user) => {
      unsubscribe();

      if (user) {
        // Session was restored
        customerIdCache = user.uid;
        resolve(user.uid);
      } else {
        // No existing session, sign in anonymously
        try {
          const credential = await signInAnonymously();
          customerIdCache = credential.user.uid;
          resolve(credential.user.uid);
        } catch (error) {
          console.error('Error signing in anonymously:', error);
          reject(error);
        }
      }
    });
  });

  return authInitPromise;
}

/**
 * Get the current customer ID synchronously
 * Returns null if auth hasn't been initialized yet
 */
export function getCustomerId(): string | null {
  return customerIdCache;
}

/**
 * Get the current customer ID, initializing auth if needed
 */
export async function getCustomerIdAsync(): Promise<string> {
  if (customerIdCache) {
    return customerIdCache;
  }
  return initCustomerAuth();
}

/**
 * Subscribe to customer auth state changes
 * Returns unsubscribe function
 */
export function onCustomerAuthStateChange(
  callback: (customerId: string | null) => void
): () => void {
  return onAuthStateChange((user) => {
    customerIdCache = user?.uid || null;
    callback(user?.uid || null);
  });
}
